import React, { CSSProperties, forwardRef, useEffect, useRef, useState } from 'react';
import { useOnScreen } from '../hooks/useOnScreen';
import { useScript } from '../hooks/useScript';
import { Spinner } from '../root/components/Spinner';

interface Props {
  videoId: string;
  responsivePadding: Pick<CSSProperties, 'padding'>;
  autoplayThreshhold?: number;
  forcePlay?: boolean;
  style?: CSSProperties;
}

export const WistiaPanel: React.FC<Props> = ({ forcePlay, autoplayThreshhold = 0.2, ...props }) => {
  const [playStarted, setPlayStarted] = useState<boolean>(forcePlay ?? false);
  const wistiaRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(wistiaRef, '0px', autoplayThreshhold);

  useEffect(() => {
    if (isOnScreen && !playStarted) {
      setPlayStarted(true);
    }
  }, [isOnScreen, playStarted]);

  return playStarted ? <_WistiaPanel {...props} /> : <WistiaPlaceholder ref={wistiaRef} {...props} />;
};

const WistiaPlaceholder = forwardRef<HTMLDivElement, Props>(({ style, responsivePadding }, ref) => {
  return (
    <div ref={ref} style={{ width: '100%', ...style }}>
      <div style={{ position: 'relative', ...responsivePadding }}>
        <Spinner visible={true} />
      </div>
    </div>
  );
});

const _WistiaPanel: React.FC<Omit<Props, 'forcePlay' | 'autoplayThreshhold'>> = React.memo(({ videoId, style, responsivePadding }) => {
  useScript(videoId ? `https://fast.wistia.com/embed/medias/${videoId}.jsonp` : undefined);
  useScript(videoId ? 'https://fast.wistia.com/assets/external/E-v1.js' : undefined);

  return (
    <div style={{ width: '100%', ...style }}>
      <div className="wistia_responsive_padding" style={{ position: 'relative', ...responsivePadding }}>
        <div className="wistia_responsive_wrapper" style={{ height: '100%', left: 0, position: 'absolute', top: 0, width: '100%' }}>
          <div className={`wistia_embed wistia_async_${videoId} videoFoam=true`} style={{ height: '100%', position: 'relative', width: '100%' }}>
            <div
              className="wistia_swatch"
              style={{ height: '100%', left: 0, opacity: 1, overflow: 'hidden', position: 'absolute', top: 0, transition: 'opacity 200ms', width: '100%' }}
            >
              <Spinner visible={true} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default WistiaPanel;
